import { IR_THEME_DEFAULT } from '@/utils/enum';
import i18n, { ZH_HK, EN, ZH } from '@/assets/js/i18n';
import { get } from 'lodash-es';
import { getLanguageType } from '@/assets/js/i18n';
import { getUuid } from '@/utils/api';
import { irstore } from '@/fetch/interface';
import axios from '@/axios';

function flattenArray(arr) {
	return arr.reduce((acc, val) => (Array.isArray(val) ? acc.concat(flattenArray(val)) : acc.concat(val)), []);
}

export const getters = {
	getIrBaseInfo: state => {
		const baseData = state.storeIrstore.templates[state.storeIrstore.activeTemplate].irBaseInfo;
		return baseData;
	},
	getIrTemplateData: state => {
		const tmplData = state.storeIrstore.templates[state.storeIrstore.activeTemplate].irTmplData;
		return tmplData;
	},
	getIrCoreConfig: state => {
		const coreData = state.storeIrstore.templates[state.storeIrstore.activeTemplate].irCoreConfig;
		return coreData || [];
	},
	getIrName: state => {
		const keyMap = {
			[ZH]: 'name',
			[ZH_HK]: 'nameZhCht',
			[EN]: 'nameEn'
		};
		const findKey = keyMap[i18n.locale];
		return get(getters.getIrBaseInfo(state), findKey);
	},
	getIrPageTitle: state => {
		const name = getters.getIrName(state);
		return i18n.t('enterprise.irstore', { name });
	},
	getIrSlogan: state => {
		const keyMap = {
			[ZH]: 'slogan',
			[ZH_HK]: 'sloganZhCht',
			[EN]: 'sloganEn'
		};
		const findKey = keyMap[i18n.locale];
		return get(getters.getIrBaseInfo(state), findKey);
	},
	getIrIndustryName: state => {
		const keyMap = {
			[ZH]: 'industry.name',
			[ZH_HK]: 'industryZhCht',
			[EN]: 'industryEn'
		};
		const findKey = keyMap[i18n.locale];
		return get(getters.getIrBaseInfo(state), findKey);
	},
	getIrPageType: state => {
		const getPageType = state.storeIrstore.activeTemplate;
		if (getPageType) {
			return Number(getPageType);
		}
		const value = sessionStorage.getItem('ir_page_type');
		return value ? Number(value) : null;
	},
	getIrGlobalModules: state => {
		const data = getters.getIrTemplateData(state);
		const { pageMenuVOS } = data;
		const filterGlobalModules = (pageMenuVOS || []).filter(v => v.isDefault).map(menu => menu.moduleVOS || []);
		const simplifiedModules = flattenArray(filterGlobalModules).map(module => ({
			...module
		}));
		return simplifiedModules;
	},
	getIrTemplateFlattenModules: state => {
		const pageType = getters.getIrPageType(state);
		const isOldH5 = pageType == 2;
		if (isOldH5) {
			const data = getters.getIrTemplateData(state);
			const { pageMenuVOS } = data;
			const filterNormalModules = (pageMenuVOS || []).map(menu => menu.moduleVOS || []);
			const simplifiedModules = flattenArray(filterNormalModules).map(module => ({
				...module
			}));
			return simplifiedModules;
		}

		const data = getters.getIrTemplateData(state);
		const { pageMenuVOS } = data;
		const filterNormalModules = (pageMenuVOS || []).filter(v => !v.isDefault).map(menu => menu.moduleVOS || []);
		const simplifiedModules = flattenArray(filterNormalModules).map(module => ({
			...module
		}));
		return simplifiedModules;
	},
	getIrThemeColor: state => {
		const data = getters.getIrTemplateData(state);
		return data.themeColorsHex || IR_THEME_DEFAULT;
	},
	getIrMarketAndCode: state => {
		const { code, market } = getters.getIrBaseInfo(state);
		return [market, code];
	},
	getIrFullCode: state => {
		const [market, code] = getters.getIrMarketAndCode(state);
		if (!code) return '';
		return `${market}${code}`;
	},
	isHKStock: state => {
		const [market, code] = getters.getIrMarketAndCode(state);
		if (!market) return false;
		return market.toLocaleUpperCase() == 'HK';
	},
	getCurrUnitTips: state => {
		return getters.isHKStock(state) ? 'HKD' : 'CNY';
	},

	getSummaryMode: state => {
		return state.storeIrstore.summaryMode;
	},
	getSummaryConfig: state => {
		return state.storeIrstore.summaryConfig;
	},
	getSummaryChartColors: state => {
		return state.storeIrstore.summaryChartColors;
	},
	getSummaryAnimationTask: state => {
		return state.storeIrstore.animationTask;
	},

	// 爱博医疗
	isEbmedical: state => {
		const { code } = getters.getIrBaseInfo(state);
		return code === '688050';
	},

	// -------------------- 重构待办
	getUiConfig: state => state.storeIrstore.rebuild.uiConfig,
	getHostConfig: state => state.storeIrstore.rebuild.hostConfig,
	getTemplateId: state => state.storeIrstore.rebuild.templateId,
	getPageId: state => state.storeIrstore.rebuild.pageId,
	getPageList: state => state.storeIrstore.rebuild.pageList
};

export const mutations = {
	setActiveTemplate(state, template) {
		if (state.storeIrstore.templates[template]) {
			state.storeIrstore.activeTemplate = Number(template);
			sessionStorage.setItem('ir_page_type', template);
		}
	},
	updateIrBaseInfo(state, data) {
		state.storeIrstore.templates[state.storeIrstore.activeTemplate].irBaseInfo = data;
	},
	updateIrTemplateData(state, data) {
		state.storeIrstore.templates[state.storeIrstore.activeTemplate].irTmplData = data;
	},
	updateIrCoreConfig(state, data) {
		state.storeIrstore.templates[state.storeIrstore.activeTemplate].irCoreConfig = data;
	},

	updateSummaryMode(state, data) {
		state.storeIrstore.summaryMode = data;
	},
	updateSummaryConfig(state, data) {
		state.storeIrstore.summaryConfig = data;
	},
	updateSummaryChartColors(state, data) {
		state.storeIrstore.summaryChartColors = data;
	},
	updateSummaryAnimationTask(state, data) {
		const { key, value } = data;
		if (!state.storeIrstore.animationTask) {
			const taskList = [];
			taskList.push({
				dom: key,
				task: value,
				uuid: getUuid()
			});
			state.storeIrstore.animationTask = taskList;
		} else {
			state.storeIrstore.animationTask.push({
				dom: key,
				task: value,
				uuid: getUuid()
			});
		}
	},
	updateEntryUrlWithToken(state, data) {
		state.storeIrstore.entryUrlWithToken = data;
	},

	setUiConfig(state, data) {
		state.storeIrstore.rebuild.uiConfig = data;
	},
	setHostConfig(state, data) {
		state.storeIrstore.rebuild.hostConfig = data;
	},
	setTemplateId(state, data) {
		state.storeIrstore.rebuild.templateId = data;
	},
	setPageId(state, data) {
		state.storeIrstore.rebuild.pageId = data;
	},
	setPageList(state, data) {
		state.storeIrstore.rebuild.pageList = data;
	}
};

export const actions = {};

// 1:PC 2:旧版H5 3:新版H5 4:侧边栏PC 5:(新)瀑布流
// 6:侧边导航 7:卡片移动
const allTypes = [1, 2, 3, 4, 5, 6, 7];
const createStoreTemplates = () => {
	return allTypes.reduce((acc, type) => {
		acc[type] = {
			irBaseInfo: {},
			irTmplData: {},
			irCoreConfig: [] // 金刚区
		};
		return acc;
	}, {});
};

export const TEMPLATE_MAP = {
	flow: 2,
	tmpl: 3,
	cardM: 7
};
export const MULTI_TEMPLATES = ['6', '7'];
export const MOB_TEMPLATES = ['2', '3', '7'];

// 反转对象的键值
export function reverseTemplateMap(obj) {
	const reversed = {};
	for (const key in obj) {
		if (obj.hasOwnProperty(key)) {
			reversed[obj[key]] = key;
		}
	}
	return reversed;
}

const storeIrstore = {
	namespaced: true,
	state: {
		templates: {
			...createStoreTemplates()
		},
		activeTemplate: '3',
		entryUrlWithToken: '',

		rebuild: {
			templateId: null,
			pageId: null,
			pageList: [],
			uiConfig: {},
			hostConfig: {}
		},

		// 投关总结
		summaryMode: '',
		summaryConfig: {},
		summaryChartColors: [],
		animationTask: []
	}
	// TODO 迁移内容过多, 暂时不在模块内定义 getters/mutations
	// getters,
	// mutations
};

export default storeIrstore;
